import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Loader from 'react-loader-spinner';
import { FiCalendar, FiEdit2, FiPlus, FiSearch } from 'react-icons/fi';
import { API_URLS, getTeamImageUrl } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { IfCanWrite } from '../utilities/adminAccess';
import { champFields, extractEntity, extractList } from '../utilities/tournamentsApi';
import {
  MATCHES_PAGE_SIZE,
  extractMatchesPage,
  formatClock,
  formatMatchDate,
  matchClock,
  moscowTodayIso,
  matchRequestBody,
  matchStatusKey,
  scoreText,
  teamLogoUrl,
} from '../utilities/matchesApi';
import {
  ROLE_REFEREE,
  extractAdminStaff,
  isEmptyRefereeId,
  staffRequestBody,
  teamCardName,
} from '../utilities/matchStaffApi';
import { kickoffFieldsForApi, resolveMatchTimezone } from '../utilities/matchKickoff';
import SearchSelect from './SearchSelect';
import { getFlagUrl } from '../utilities/flags';
import ReorderableTable from './ReorderableTable';
import StadiumCrumbs from './StadiumCrumbs';
import CreateMatchModal from './CreateMatchModal';
import CustomCalendar from './CustomCalendar';
import useComponentVisible from '../utilities/useComponentVisible';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import '../styles/stadiumsManagement.css';
import '../styles/gamesManagement.css';

const FILTER_OPTIONS = [
  { value: 'my', labelKey: 'gmMyGames' },
  { value: 'nolineup', labelKey: 'gmNoLineup' },
  { value: 'unplayed', labelKey: 'gmUnplayed' },
  { value: 'nodate', labelKey: 'gmNoDate' },
];

const groupMatchesByCountry = (list) => {
  const map = new Map();
  const order = [];
  list.forEach((match) => {
    const key = String(match.country_id || 0);
    if (!map.has(key)) {
      map.set(key, {
        country_id: match.country_id || 0,
        country_name: match.country_name || '',
        country_flag: match.country_flag || '',
        matches: [],
      });
      order.push(key);
    }
    map.get(key).matches.push(match);
  });
  return order.map((key) => map.get(key));
};

const mergeMatchGroups = (prev, next) => {
  if (!next.length) return prev;
  const map = new Map();
  const order = [];
  [...prev, ...next].forEach((group) => {
    const key = String(group.country_id);
    const existing = map.get(key);
    if (existing) {
      existing.matches = existing.matches.concat(group.matches);
      return;
    }
    map.set(key, { ...group, matches: group.matches.slice() });
    order.push(key);
  });
  return order.map((key) => map.get(key));
};

const pad2 = (value) => String(value).padStart(2, '0');

const toIsoDate = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const isoToDate = (iso) => {
  const [year, month, day] = String(iso || '').split('-').map(Number);
  if (!year || !month || !day) return new Date();
  return new Date(year, month - 1, day);
};

const shiftIso = (iso, days) => {
  const date = isoToDate(iso);
  date.setDate(date.getDate() + days);
  return toIsoDate(date);
};

const todayIso = () => moscowTodayIso();

const readFilters = () => {
  const params = new URLSearchParams(window.location.search);
  const filter = params.get('filter') || '';
  const date = params.get('date');
  return {
    search: params.get('search') || '',
    country: params.get('country') || '',
    league: params.get('league') || '',
    season: params.get('season') || '',
    filter: FILTER_OPTIONS.some((item) => item.value === filter) ? filter : '',
    date: date === 'all' ? '' : (/^\d{4}-\d{2}-\d{2}$/.test(date || '') ? date : todayIso()),
  };
};

// ─── Быстрое редактирование (как в админке ingame) ───
const QUICK_FIELDS = ['date', 'time', 'half_time', 'stadium_id', 'pitch_id', 'judje_id'];

const digits2 = (value) => String(value ?? '').replace(/[^0-9]/g, '').slice(0, 2);

const refereeIdOf = (item) => {
  const fromStaff = (item.staff || []).find((person) => person?.slot === 'judje_id');
  const id = item.judje_id ?? item.referee?.id ?? fromStaff?.id ?? fromStaff?.user_id;
  return isEmptyRefereeId(id) ? '' : String(id);
};

const quickOriginal = (item) => ({
  date: item.no_date || !item.date ? '' : String(item.date).slice(0, 10),
  time: item.no_date ? '' : matchClock(item.time),
  half_time: String(Number(item.half_time) > 0 ? Number(item.half_time) : 30),
  stadium_id: item.stadium?.id ? String(item.stadium.id) : '',
  pitch_id: item.pitch?.id ? String(item.pitch.id) : '',
  judje_id: refereeIdOf(item),
});

const personLabel = (person) => (
  [person.family, person.name].filter(Boolean).join(' ') || person.login || `#${person.id}`
);

const teamFullName = (team) => team?.name || teamCardName(team) || '—';
const teamShortName = (team) => teamCardName(team) || team?.name || '—';

export default function MatchCenter() {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const { champId, selected: championship, loading: champsLoading } = useChampionships();
  const initial = readFilters();
  const {
    ref: calendarRef,
    isComponentVisible: calendarOpen,
    setIsComponentVisible: setCalendarOpen,
  } = useComponentVisible(false);

  const [matches, setMatches] = useState([]);
  const [matchGroups, setMatchGroups] = useState([]);
  const [total, setTotal] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState(initial.search);
  const [countryId, setCountryId] = useState(initial.country);
  const [leagueId, setLeagueId] = useState(initial.league);
  const [seasonId, setSeasonId] = useState(initial.season);
  const [listFilter, setListFilter] = useState(initial.filter);
  const [selectedDate, setSelectedDate] = useState(initial.date);
  const [countries, setCountries] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [createOpen, setCreateOpen] = useState(false);
  // Быстрое редактирование: строки не ведут в матч, а превращаются в поля. Справочники грузим при входе в режим.
  const [quickEdit, setQuickEdit] = useState(false);
  const [quickEdits, setQuickEdits] = useState({});
  const [quickSaving, setQuickSaving] = useState(false);
  const [quickError, setQuickError] = useState('');
  const [refsLoading, setRefsLoading] = useState(false);
  const [quickStadiums, setQuickStadiums] = useState([]);
  const [quickReferees, setQuickReferees] = useState([]);
  const [pitchesByStadium, setPitchesByStadium] = useState({});
  const pitchRequestsRef = useRef({});
  const searchTimer = useRef(null);
  const requestIdRef = useRef(0);
  const matchesRef = useRef([]);
  const groupsRef = useRef([]);
  matchesRef.current = matches;
  groupsRef.current = matchGroups;

  const prevChampRef = useRef(null);
  const today = todayIso();
  const yesterday = shiftIso(today, -1);
  const tomorrow = shiftIso(today, 1);

  useEffect(() => {
    if (!champId && champsLoading) return undefined;
    if (prevChampRef.current && champId && prevChampRef.current !== champId) {
      setCountryId('');
      setLeagueId('');
      setSeasonId('');
      setLeagues([]);
      setSeasons([]);
    }
    prevChampRef.current = champId || null;
    loadCountries();
    return undefined;
  }, [champId, champsLoading]);

  useEffect(() => {
    if (countryId) loadLeagues(countryId);
    else {
      setLeagues([]);
      setSeasons([]);
    }
  }, [countryId]);

  useEffect(() => {
    if (leagueId) loadSeasons(leagueId);
    else setSeasons([]);
  }, [leagueId]);

  useEffect(() => {
    writeUrl({
      search: searchQuery,
      country: countryId,
      league: leagueId,
      season: seasonId,
      filter: listFilter,
      date: selectedDate,
    });
  }, [searchQuery, countryId, leagueId, seasonId, listFilter, selectedDate]);

  useEffect(() => {
    if (!champId && champsLoading) return undefined;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const delay = searchQuery ? 350 : 0;
    searchTimer.current = setTimeout(() => {
      loadMatches(0, false);
    }, delay);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchQuery, countryId, leagueId, seasonId, listFilter, champId, champsLoading, selectedDate]);

  const writeUrl = (next) => {
    const params = new URLSearchParams();
    if (next.search) params.set('search', next.search);
    if (next.country) params.set('country', String(next.country));
    if (next.league) params.set('league', String(next.league));
    if (next.season) params.set('season', String(next.season));
    if (next.filter) params.set('filter', next.filter);
    params.set('date', next.date || 'all');
    const query = params.toString();
    window.history.replaceState({}, '', query ? `${window.location.pathname}?${query}` : window.location.pathname);
  };

  const loadCountries = async () => {
    if (!champId) {
      setCountries([]);
      return;
    }
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountries, champFields(champId));
      setCountries(extractList(response.data));
    } catch (error) {
      setCountries([]);
    }
  };

  const loadLeagues = async (nextCountryId) => {
    if (!nextCountryId) {
      setLeagues([]);
      setSeasons([]);
      return;
    }
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountry, { id: Number(nextCountryId) });
      setLeagues(extractList(extractEntity(response.data).leagues));
    } catch (error) {
      setLeagues([]);
    }
  };

  const loadSeasons = async (nextLeagueId) => {
    if (!nextLeagueId) {
      setSeasons([]);
      return;
    }
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminLeague, {
        id: Number(nextLeagueId),
        ...champFields(champId),
      });
      const league = extractEntity(response.data);
      const list = extractList(league.seasons);
      setSeasons(list);
      setSeasonId((current) => {
        if (current && list.some((item) => Number(item.id || item.season_id) === Number(current))) {
          return current;
        }
        const activeId = league.active_season || league.active_season_id;
        if (activeId && list.some((item) => Number(item.id || item.season_id) === Number(activeId))) {
          return String(activeId);
        }
        return '';
      });
    } catch (error) {
      setSeasons([]);
    }
  };

  const handleCountryChange = (value) => {
    setCountryId(value);
    setLeagueId('');
    setSeasonId('');
  };

  const handleLeagueChange = (value) => {
    setLeagueId(value);
    setSeasonId('');
  };

  const loadMatches = async (offset, append) => {
    const requestId = ++requestIdRef.current;
    if (!champId) {
      setMatches([]);
      setMatchGroups([]);
      setTotal(null);
      setHasMore(false);
      setLoading(false);
      setMessage({ text: champsLoading ? '' : t('gmNeedChamp'), type: champsLoading ? '' : 'error' });
      return;
    }

    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      setMessage({ text: '', type: '' });
    }

    try {
      const body = matchRequestBody(champId, {
        limit: MATCHES_PAGE_SIZE,
        offset,
      });
      if (listFilter === 'nodate') body.no_date = true;
      else if (selectedDate) body.date = selectedDate;
      if (searchQuery.trim()) body.search = searchQuery.trim();
      if (countryId) body.country_id = Number(countryId);
      else body.group_by = 'country';
      if (leagueId) body.league_id = Number(leagueId);
      if (seasonId) body.season_id = Number(seasonId);
      if (listFilter === 'my') body.my_games = true;
      if (listFilter === 'nolineup') body.no_lineup = true;
      if (listFilter === 'unplayed') body.unplayed = true;

      const response = await authorizedPost(dispatch, API_URLS.getAdminMatches, body);
      if (requestId !== requestIdRef.current) return;
      const page = extractMatchesPage(response.data);
      const next = append ? matchesRef.current.concat(page.matches) : page.matches;
      const nextGroups = page.groups.length
        ? (append ? mergeMatchGroups(groupsRef.current, page.groups) : page.groups)
        : [];
      setMatches(next);
      setMatchGroups(nextGroups);
      setTotal(page.total);
      setHasMore(page.total != null ? next.length < page.total : page.matches.length === MATCHES_PAGE_SIZE);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      if (!append) {
        setMatches([]);
        setMatchGroups([]);
        setTotal(null);
        setHasMore(false);
      }
      setMessage({ text: getApiErrorMessage(error, t('gmLoadError')), type: 'error' });
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const changeDate = (nextDate) => {
    const iso = typeof nextDate === 'string' ? nextDate : toIsoDate(nextDate);
    setCalendarOpen(false);
    if (listFilter === 'nodate') setListFilter('');
    setSelectedDate(iso || '');
  };

  const handleFilterChange = (value) => {
    setListFilter(value);
    if (value === 'nodate') setSelectedDate('');
  };

  const dateClass = (iso) => (
    listFilter === 'nodate' ? '' : (selectedDate === iso ? 'matcheCenterMenuItemActive' : '')
  );
  const calendarActive = listFilter !== 'nodate' && Boolean(selectedDate) && selectedDate !== yesterday && selectedDate !== today && selectedDate !== tomorrow;

  const handleCreatedMatch = (createdDate) => {
    const iso = /^\d{4}-\d{2}-\d{2}$/.test(String(createdDate || '').slice(0, 10))
      ? String(createdDate).slice(0, 10)
      : '';
    if (iso && listFilter === 'nodate') setListFilter('');
    if (iso === selectedDate) loadMatches(0, false);
    else setSelectedDate(iso);
  };

  const statusClass = (match) => {
    const key = matchStatusKey(match);
    if (key === 'matchFinished' || key === 'technicalDefeat') return 'is-done';
    if (key === 'matchPostponed' || key === 'halfTime') return 'is-warn';
    if (key === 'firstHalf' || key === 'secondHalf' || key === 'extraFirstHalf' || key === 'extraSecondHalf') return 'is-live';
    return '';
  };

  const renderTeamName = (team) => (
    <>
      <span className="gm-team-name gm-team-name-full">{teamFullName(team)}</span>
      <span className="gm-team-name gm-team-name-short">{teamShortName(team)}</span>
    </>
  );

  const renderTeam = (team, side, item) => {
    const pending = side === 'home'
      ? Boolean(item.has_home_cap_lineup) && !item.has_home_lineup
      : Boolean(item.has_guest_cap_lineup) && !item.has_guest_lineup;
    return (
      <div className={`gm-team is-${side}`}>
        {side === 'home' ? renderTeamName(team) : null}
        {pending && side === 'home' ? <span className="gm-cap-mark" title={t('gmCaptainLineupPending')}>{t('gmCaptainShort')}</span> : null}
        <img
          className="gm-logo"
          src={teamLogoUrl(team) || getTeamImageUrl(team?.image) || defaultTeamLogo}
          alt=""
          onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
        />
        {pending && side === 'guest' ? <span className="gm-cap-mark" title={t('gmCaptainLineupPending')}>{t('gmCaptainShort')}</span> : null}
        {side === 'guest' ? renderTeamName(team) : null}
      </div>
    );
  };

  // ═══ Быстрое редактирование ═══
  const stadiumOptions = useMemo(
    () => quickStadiums.map((item) => ({ value: item.id, label: item.name || `#${item.id}` })),
    [quickStadiums]
  );
  const refereeOptions = useMemo(
    () => quickReferees.map((person) => ({ value: person.id, label: personLabel(person) })),
    [quickReferees]
  );

  const loadPitches = async (stadiumId) => {
    const key = String(stadiumId || '');
    if (!key || pitchRequestsRef.current[key]) return;
    pitchRequestsRef.current[key] = true;
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminStadium, {
        id: Number(key),
        ...champFields(champId),
      });
      const list = extractList(extractEntity(response.data).pitches);
      setPitchesByStadium((prev) => ({ ...prev, [key]: list }));
    } catch (error) {
      setPitchesByStadium((prev) => ({ ...prev, [key]: [] }));
    }
  };

  // Поля стадионов — для всех стадионов на странице (в т.ч. после «Показать ещё»).
  useEffect(() => {
    if (!quickEdit) return;
    matches.forEach((item) => {
      const stadiumId = quickEdits[item.id]?.stadium_id ?? (item.stadium?.id ? String(item.stadium.id) : '');
      if (stadiumId) loadPitches(stadiumId);
    });
  }, [quickEdit, matches, quickEdits]); // eslint-disable-line react-hooks/exhaustive-deps

  const enterQuickEdit = async () => {
    setQuickEdit(true);
    setQuickEdits({});
    setQuickError('');
    if (quickStadiums.length && quickReferees.length) return;
    setRefsLoading(true);
    try {
      const [stadiumsRes, staffRes] = await Promise.all([
        authorizedPost(dispatch, API_URLS.getAllAdminStadiums, champFields(champId)),
        authorizedPost(dispatch, API_URLS.getAdminStaff, staffRequestBody(champId, { role: ROLE_REFEREE, with_games: false })),
      ]);
      const stadiumList = Array.isArray(stadiumsRes.data) ? stadiumsRes.data : extractList(stadiumsRes.data);
      setQuickStadiums(stadiumList.filter((item) => item && item.id));
      setQuickReferees(extractAdminStaff(staffRes.data).filter((person) => Number(person.role) === ROLE_REFEREE));
    } catch (error) {
      setQuickError(getApiErrorMessage(error, t('gmQuickRefsError')));
    } finally {
      setRefsLoading(false);
    }
  };

  const cancelQuickEdit = () => {
    setQuickEdit(false);
    setQuickEdits({});
    setQuickError('');
  };

  const quickValue = (item, field) => {
    const patch = quickEdits[item.id];
    if (patch && patch[field] !== undefined) return patch[field];
    return quickOriginal(item)[field];
  };

  const setQuickValue = (item, field, value) => {
    setQuickEdits((prev) => {
      const patch = { ...(prev[item.id] || {}), [field]: value };
      // Поле принадлежит стадиону: сменили стадион — поле сбрасываем.
      if (field === 'stadium_id') patch.pitch_id = '';
      return { ...prev, [item.id]: patch };
    });
    if (field === 'stadium_id' && value) loadPitches(value);
  };

  /** Только изменённые поля матча в формате API; null — очистить. */
  const quickChanges = (item) => {
    const patch = quickEdits[item.id];
    if (!patch) return null;
    const original = quickOriginal(item);
    const next = { ...original, ...patch };
    const changed = QUICK_FIELDS.filter((field) => String(next[field] ?? '') !== String(original[field] ?? ''));
    if (!changed.length) return null;

    const row = { match_id: item.id };
    if (changed.includes('date') || changed.includes('time')) {
      // Как в карточке матча: стенное время + пояс площадки, в UTC переводит сервер.
      const stadiumObj = quickStadiums.find((stadium) => String(stadium.id) === String(next.stadium_id)) || item.stadium;
      const kickoff = kickoffFieldsForApi(next.date, next.time);
      row.date = kickoff.date;
      row.time = kickoff.time;
      row.timezone = resolveMatchTimezone(
        stadiumObj,
        item,
        item.league,
        championship,
        item.country_name || item.city_name,
      );
    }
    if (changed.includes('half_time')) {
      const half = Number(next.half_time);
      if (!Number.isFinite(half) || half <= 0) return { match_id: item.id, half_time: 0 };
      row.half_time = Math.round(half);
    }
    if (changed.includes('stadium_id')) {
      row.stadium_id = next.stadium_id ? Number(next.stadium_id) : null;
      row.pitch_id = next.pitch_id ? Number(next.pitch_id) : null;
    } else if (changed.includes('pitch_id')) {
      row.pitch_id = next.pitch_id ? Number(next.pitch_id) : null;
    }
    if (changed.includes('judje_id')) row.judje_id = next.judje_id ? Number(next.judje_id) : null;
    return row;
  };

  const quickRows = () => matches.map(quickChanges).filter(Boolean);
  const quickChangedCount = quickEdit ? quickRows().length : 0;

  const matchTitle = (matchId) => {
    const item = matches.find((entry) => Number(entry.id) === Number(matchId));
    return item ? `${teamShortName(item.home_team)} — ${teamShortName(item.guest_team)}` : `#${matchId}`;
  };

  // Fallback, если updateMany недоступен (404).
  const saveRowsOneByOne = async (rows) => {
    const updated = [];
    const failed = [];
    for (const row of rows) {
      const { match_id: id, ...fields } = row;
      try {
        await authorizedPost(dispatch, API_URLS.changeAdminMatch, matchRequestBody(champId, { id, ...fields }));
        updated.push(id);
      } catch (error) {
        failed.push({ match_id: id, error: getApiErrorMessage(error, t('gmSaveError')) });
      }
    }
    return { updated, failed };
  };

  const saveQuickEdits = async () => {
    const rows = quickRows();
    if (!rows.length) {
      cancelQuickEdit();
      return;
    }
    const noDate = rows.find((row) => row.time && !row.date);
    if (noDate) {
      setQuickError(t('gmQuickNeedDate', { match: matchTitle(noDate.match_id) }));
      return;
    }
    const badHalf = rows.find((row) => (
      Object.prototype.hasOwnProperty.call(row, 'half_time')
      && !(Number(row.half_time) > 0)
    ));
    if (badHalf) {
      setQuickError(t('gmQuickNeedHalfTime', { match: matchTitle(badHalf.match_id) }));
      return;
    }
    setQuickSaving(true);
    setQuickError('');
    try {
      let result;
      try {
        const response = await authorizedPost(dispatch, API_URLS.updateManyMatches, matchRequestBody(champId, { matches: rows }));
        result = response.data || {};
      } catch (error) {
        if (error?.response?.status !== 404) throw error;
        result = await saveRowsOneByOne(rows);
      }
      const failed = Array.isArray(result.failed) ? result.failed : [];
      if (failed.length) {
        const failedIds = new Set(failed.map((item) => String(item.match_id ?? item)));
        // Сохранённые убираем из правок, несохранённые оставляем — можно поправить и сохранить ещё раз.
        setQuickEdits((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => failedIds.has(String(id)))));
        setQuickError(t('gmQuickFailed', {
          count: failed.length,
          list: failed.map((item) => `${matchTitle(item.match_id ?? item)}${item.error ? ` (${item.error})` : ''}`).join('; '),
        }));
        loadMatches(0, false);
        return;
      }
      cancelQuickEdit();
      setMessage({ text: t('gmQuickSaved', { count: rows.length }), type: 'success' });
      loadMatches(0, false);
    } catch (error) {
      setQuickError(getApiErrorMessage(error, t('gmSaveError')));
    } finally {
      setQuickSaving(false);
    }
  };

  const quickDateCell = (item) => (
    <div className="gm-quick-cell" onClick={(event) => event.stopPropagation()}>
      <input
        type="date"
        className="st-input gm-quick-input"
        value={quickValue(item, 'date')}
        onChange={(event) => setQuickValue(item, 'date', event.target.value)}
      />
    </div>
  );

  const quickTimeCell = (item) => (
    <div className="gm-quick-cell" onClick={(event) => event.stopPropagation()}>
      <input
        type="time"
        className="st-input gm-quick-input"
        value={quickValue(item, 'time')}
        onChange={(event) => setQuickValue(item, 'time', event.target.value)}
      />
    </div>
  );

  const quickHalfTimeCell = (item) => (
    <div className="gm-quick-cell" onClick={(event) => event.stopPropagation()}>
      <input
        type="text"
        inputMode="numeric"
        className="st-input gm-quick-input gm-quick-half"
        title={t('gmHalfTime')}
        aria-label={t('gmHalfTime')}
        value={quickValue(item, 'half_time')}
        onChange={(event) => setQuickValue(item, 'half_time', digits2(event.target.value))}
      />
    </div>
  );

  const quickStadiumCell = (item) => {
    const stadiumId = quickValue(item, 'stadium_id');
    const pitches = stadiumId ? (pitchesByStadium[String(stadiumId)] || []) : [];
    return (
      <div className="gm-quick-cell gm-quick-cell-col" onClick={(event) => event.stopPropagation()}>
        <SearchSelect
          value={stadiumId}
          options={stadiumOptions}
          emptyLabel={t('gmQuickNoStadium')}
          placeholder={t('gmQuickStadiumPh')}
          onChange={(value) => setQuickValue(item, 'stadium_id', value)}
        />
        {pitches.length ? (
          <select
            className="st-select gm-quick-input"
            value={quickValue(item, 'pitch_id')}
            onChange={(event) => setQuickValue(item, 'pitch_id', event.target.value)}
          >
            <option value="">{t('gmQuickNoPitch')}</option>
            {pitches.map((pitch) => (
              <option key={pitch.id} value={pitch.id}>{pitch.name}</option>
            ))}
          </select>
        ) : null}
      </div>
    );
  };

  const quickRefereeCell = (item) => (
    <div className="gm-quick-cell" onClick={(event) => event.stopPropagation()}>
      <SearchSelect
        value={quickValue(item, 'judje_id')}
        options={refereeOptions}
        emptyLabel={t('gmQuickNoReferee')}
        placeholder={t('gmQuickRefereePh')}
        onChange={(value) => setQuickValue(item, 'judje_id', value)}
      />
    </div>
  );

  const columns = [
    {
      key: 'date',
      label: t('gmDate'),
      className: 'st-cell-muted gm-cell-date',
      render: (item) => (
        <div className="gm-date-cell">
          {quickEdit ? quickDateCell(item) : (
            item.no_date || !item.date
              ? <span className="gm-no-date">{t('gmNoDate')}</span>
              : <span>{formatMatchDate(item.date)}</span>
          )}
          {/* Тур (стадия) — особенно важен для матчей без даты */}
          {item.stage_name ? <span className="gm-row-tour" title={item.league_name || ''}>{item.stage_name}</span> : null}
        </div>
      ),
    },
    {
      key: 'time',
      label: t('time'),
      className: quickEdit ? 'gm-cell-time' : 'gm-cell-time hide-sm',
      render: (item) => (quickEdit ? quickTimeCell(item) : formatClock(item.time)),
    },
    ...(quickEdit ? [{
      key: 'half_time',
      label: t('gmQuickHalf'),
      className: 'gm-cell-half',
      render: (item) => quickHalfTimeCell(item),
    }] : []),
    {
      key: 'match',
      label: t('match'),
      className: 'gm-cell-match',
      render: (item) => (
        <div className="gm-match-cell">
          {renderTeam(item.home_team, 'home', item)}
          <span className="gm-score">{scoreText(item)}</span>
          {renderTeam(item.guest_team, 'guest', item)}
        </div>
      ),
    },
    {
      key: 'stadium',
      label: t('stadium'),
      className: quickEdit ? 'gm-cell-stadium' : 'st-cell-muted gm-cell-stadium hide-sm',
      render: (item) => (quickEdit ? quickStadiumCell(item) : (
        <span>
          {item.stadium?.name || '—'}
          {item.pitch?.name ? <span className="gm-muted"> · {item.pitch.name}</span> : null}
        </span>
      )),
    },
    quickEdit ? {
      key: 'referee',
      label: t('gmQuickReferee'),
      className: 'gm-cell-referee',
      render: (item) => quickRefereeCell(item),
    } : {
      key: 'status',
      label: t('status'),
      className: 'st-cell-status hide-sm',
      render: (item) => (
        <span className={`gm-status ${statusClass(item)}`}>{t(matchStatusKey(item))}</span>
      ),
    },
  ];

  const foundCount = total != null ? total : matches.length;
  const groupedList = countryId
    ? null
    : (matchGroups.length ? matchGroups : groupMatchesByCountry(matches));

  const renderCountryHead = (group) => {
    const src = getFlagUrl(group.country_flag || group.country_name);
    return (
      <div className="gm-country-head">
        {src ? <img className="gm-country-flag" src={src} alt="" /> : null}
        <span>{group.country_name || '—'}</span>
      </div>
    );
  };

  const renderMatchesTable = (items, empty) => (
    <ReorderableTable
      tableClassName={`gm-table-matches${quickEdit ? ' gm-quick-edit' : ''}`}
      columns={columns}
      items={items}
      getRowKey={(item, index) => item.id || index}
      reorderMode={false}
      // В обычном режиме — Link (cmd/ctrl+click, новая вкладка). В quickEdit строки не кликабельны.
      getRowHref={(item) => (!quickEdit && item.id ? `/manage_match/${item.id}` : null)}
      empty={empty}
    />
  );

  const emptyState = (
    <div className="st-empty">
      <h3>{t('gmEmpty')}</h3>
      <p>{t('gmEmptyHint')}</p>
    </div>
  );

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page gm-list-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={[{ label: t('navMatches') }]} />
            <IfCanWrite entity="matches">
              <div className="gm-header-actions">
                {quickEdit ? (
                  <>
                    <button type="button" className="st-btn st-btn-secondary" disabled={quickSaving} onClick={cancelQuickEdit}>
                      {t('cancel')}
                    </button>
                    <button type="button" className="st-btn st-btn-primary" disabled={quickSaving || refsLoading} onClick={saveQuickEdits}>
                      {quickSaving
                        ? t('gmQuickSaving')
                        : (quickChangedCount ? t('gmQuickSaveCount', { count: quickChangedCount }) : t('save'))}
                    </button>
                  </>
                ) : (
                  <>
                    {/* Активна, только когда на странице есть хотя бы один матч */}
                    <button
                      type="button"
                      className="st-btn st-btn-secondary"
                      disabled={loading || matches.length === 0}
                      title={!loading && matches.length === 0 ? t('gmQuickNoMatches') : undefined}
                      onClick={enterQuickEdit}
                    >
                      <FiEdit2 /> {t('gmQuickEdit')}
                    </button>
                    <button
                      type="button"
                      className="st-btn st-btn-primary"
                      onClick={() => setCreateOpen(true)}
                    >
                      <FiPlus /> {t('createMatch')}
                    </button>
                  </>
                )}
              </div>
            </IfCanWrite>
          </div>
          {quickEdit ? (
            <div className="gm-quick-note">
              {refsLoading ? t('gmQuickLoadingRefs') : t('gmQuickHint')}
              {quickError ? <div className="gm-quick-error">{quickError}</div> : null}
            </div>
          ) : null}
        </div>

        <div className="gm-toolbar">
          <div className="gm-topbar">
            <div className="st-search">
              <FiSearch className="st-search-icon" />
              <input
                type="text"
                className="st-search-input"
                placeholder={t('gmSearchPh')}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
              {searchQuery ? (
                <button type="button" className="st-search-clear" onClick={() => setSearchQuery('')}>×</button>
              ) : null}
            </div>
            <div className="gm-dates">
              <div className="gm-date-calendar" ref={calendarRef}>
                <div
                  className={`calendarIconWrapper ${calendarActive ? 'matcheCenterCalendarActive' : ''}`}
                  onClick={() => setCalendarOpen(!calendarOpen)}
                >
                  <FiCalendar size={26} />
                  {selectedDate ? <span className="dateNumber">{isoToDate(selectedDate).getDate()}</span> : null}
                </div>
                {calendarOpen ? (
                  <div className="gm-calendar">
                    <CustomCalendar
                      currentDate={selectedDate ? isoToDate(selectedDate) : new Date()}
                      changeDate={(value) => changeDate(value)}
                    />
                  </div>
                ) : null}
              </div>
              <span
                className={`matcheCenterMenuItem ${dateClass('')}`}
                onClick={() => changeDate('')}
              >
                {t('gmAllDates')}
              </span>
              <span
                className={`matcheCenterMenuItem ${dateClass(yesterday)}`}
                onClick={() => changeDate(yesterday)}
              >
                {t('yesterday')}
              </span>
              <span
                className={`matcheCenterMenuItem ${dateClass(today)}`}
                onClick={() => changeDate(today)}
              >
                {t('today')}
              </span>
              <span
                className={`matcheCenterMenuItem ${dateClass(tomorrow)}`}
                onClick={() => changeDate(tomorrow)}
              >
                {t('tomorrow')}
              </span>
            </div>
          </div>
          <div className="gm-filters">
            <select
              className="st-select gm-filter-select"
              value={listFilter}
              onChange={(event) => handleFilterChange(event.target.value)}
            >
              <option value="">{t('gmFilterAll')}</option>
              {FILTER_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>{t(item.labelKey)}</option>
              ))}
            </select>
            <select
              className="st-select gm-filter-select"
              value={countryId}
              onChange={(event) => handleCountryChange(event.target.value)}
            >
              <option value="">{t('gmAllCountries')}</option>
              {countries.map((country) => (
                <option key={country.id} value={country.id}>{country.name}</option>
              ))}
            </select>
            <select
              className="st-select gm-filter-select"
              value={leagueId}
              disabled={!countryId}
              onChange={(event) => handleLeagueChange(event.target.value)}
            >
              <option value="">{t('gmAllLeagues')}</option>
              {leagues.map((league) => (
                <option key={league.id || league.league_id} value={league.id || league.league_id}>
                  {league.name || league.league_name}
                </option>
              ))}
            </select>
            <select
              className="st-select gm-filter-select"
              value={seasonId}
              disabled={!leagueId}
              onChange={(event) => setSeasonId(event.target.value)}
            >
              <option value="">{t('gmAllSeasons')}</option>
              {seasons.map((season) => (
                <option key={season.id || season.season_id} value={season.id || season.season_id}>
                  {season.name || season.season_name}
                </option>
              ))}
            </select>
            <div className="st-meta">
              <strong>{t('gmFound', { count: foundCount })}</strong>
            </div>
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll">
          <div className="st-content">
            <div className="st-block">
              {loading ? (
                <div className="st-loading">
                  <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                  <p>{t('loadingMatches')}</p>
                </div>
              ) : (
                <>
                  {groupedList && groupedList.length ? (
                    groupedList.map((group) => (
                      <div key={group.country_id} className="gm-country-group">
                        {renderCountryHead(group)}
                        {renderMatchesTable(group.matches, null)}
                      </div>
                    ))
                  ) : (
                    renderMatchesTable(matches, emptyState)
                  )}
                  {hasMore ? (
                    <div className="gm-load-more">
                      <button
                        type="button"
                        className="st-btn st-btn-secondary"
                        disabled={loadingMore}
                        onClick={() => loadMatches(matches.length, true)}
                      >
                        {loadingMore ? t('loadingMatches') : t('gmLoadMore')}
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>

        <CreateMatchModal
          isOpen={createOpen}
          onClose={() => setCreateOpen(false)}
          onSuccess={handleCreatedMatch}
        />
      </div>
    </div>
  );
}
